import React, { useState, useEffect, useMemo } from 'react';
import apiUser from '../../../../apiUser';
import LoadingSpinner from '../../../Loader/load';
import { listaCompetencias } from './competencias';

const fmtBRL = (v) => (v == null ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
const fmtCnpj = (v) => {
    const d = String(v || '').replace(/\D/g, '');
    return d.length === 14 ? d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5') : (v || '—');
};
const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const fmtComp = (c) => (c ? `${MESES[Number(String(c).slice(5, 7)) - 1]}/${String(c).slice(0, 4)}` : '—');

const TIPO_EVENTO = { reajuste: 'Reajuste', aditivo: 'Aditivo', supressao: 'Supressão', renovacao: 'Renovação', homologacao: 'Homologação' };
const resumoEvento = (e) =>
    `${TIPO_EVENTO[e.tipo] || e.tipo}${e.percentual != null ? ` ${Number(e.percentual) > 0 ? '+' : ''}${Number(e.percentual)}%` : ''}${e.valor != null ? ` ${fmtBRL(e.valor)}` : ''} em ${String(e.data_evento).slice(8, 10)}/${String(e.data_evento).slice(5, 7)}`;

const BadgeEvento = ({ eventos }) => {
    if (!eventos || !eventos.length) return null;
    return (
        <span className='ml-2 inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 cursor-help'
            title={eventos.map(resumoEvento).join(' · ')}>
            ⚡ evento no mês
        </span>
    );
};

const Variacao = ({ v }) => {
    if (v == null) return <span className='text-gray-400'>—</span>;
    const cls = v > 0 ? 'bg-green-100 text-green-800' : v < 0 ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-700';
    return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{v > 0 ? '+' : ''}{v.toFixed(2)}%</span>;
};

const Card = ({ titulo, valor, destaque }) => (
    <div className={`rounded-xl border p-4 ${destaque ? 'bg-vox-blue-600 text-white border-vox-blue-700' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}>
        <div className={`text-xs uppercase tracking-wide ${destaque ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>{titulo}</div>
        <div className='text-xl font-bold mt-1'>{valor}</div>
    </div>
);

// Tipo de faturamento do CLIENTE, derivado dos contratos: Global | Por Item | Misto.
const tipoDoCliente = (c) => {
    const tipos = new Set(c.contratos.map((ct) => ct.tipo));
    if (tipos.size > 1) return 'Misto';
    return tipos.has('global') ? 'Global' : 'Por Item';
};

const FaturamentoTotal = () => {
    const [dados, setDados] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busca, setBusca] = useState('');
    const [filtroTipo, setFiltroTipo] = useState(''); // '' | Global | Por Item | Misto
    const [ordem, setOrdem] = useState({ key: 'cliente_nome', dir: 1 }); // dir: 1 asc, -1 desc
    const [abertos, setAbertos] = useState({}); // { [cliente_id]: bool }
    const [competencia, setCompetencia] = useState(''); // vazio = corrente
    const [corrente, setCorrente] = useState(null);
    const [erro, setErro] = useState(null);

    useEffect(() => {
        let ignorar = false;
        setLoading(true);
        setErro(null);
        apiUser.get('/faturamento/relatorios/faturamento-total', { params: competencia ? { competencia } : {} })
            .then(({ data }) => {
                if (ignorar) return;
                if (!data.success) { setErro(data.message || 'Não foi possível carregar o relatório.'); return; }
                setDados(data);
                if (!competencia) setCorrente(data.competencia_atual);
            })
            .catch(() => { if (!ignorar) setErro('Não foi possível carregar o relatório.'); })
            .finally(() => { if (!ignorar) setLoading(false); });
        return () => { ignorar = true; };
    }, [competencia]);

    const ordenar = (key) =>
        setOrdem((o) => ({ key, dir: o.key === key ? -o.dir : 1 }));

    const clientes = useMemo(() => {
        if (!dados) return [];
        const q = busca.trim().toLowerCase();
        let lista = dados.clientes.map((c) => ({ ...c, tipo_faturamento: tipoDoCliente(c) }));
        if (q) {
            lista = lista.filter((c) =>
                String(c.cliente_nome).toLowerCase().includes(q) ||
                String(c.cnpj || '').replace(/\D/g, '').includes(q.replace(/\D/g, '') || '###'));
        }
        if (filtroTipo) lista = lista.filter((c) => c.tipo_faturamento === filtroTipo);
        const { key, dir } = ordem;
        const chave = { cliente_nome: 'cliente_nome', cnpj: 'cnpj', tipo_faturamento: 'tipo_faturamento',
            valor_contratado: 'valor_contratado', valor_atual: 'valor_atual',
            valor_anterior: 'valor_anterior', variacao: 'variacao' }[key] || 'cliente_nome';
        return [...lista].sort((a, b) => {
            const va = a[chave], vb = b[chave];
            if (va == null && vb == null) return 0;
            if (va == null) return 1;   // nulos sempre no fim
            if (vb == null) return -1;
            if (typeof va === 'number' || typeof vb === 'number') return (Number(va) - Number(vb)) * dir;
            return String(va).localeCompare(String(vb), 'pt-BR') * dir;
        });
    }, [dados, busca, filtroTipo, ordem]);

    if (!dados) {
        if (loading) return <LoadingSpinner />;
        return <p className='p-4 text-gray-500'>{erro || 'Não foi possível carregar o relatório.'}</p>;
    }
    const t = dados.totais || {};

    const thCls = 'px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300';
    const tdNum = 'px-3 py-2 text-right whitespace-nowrap';

    return (
        <div className='p-4 space-y-4'>
            {/* Totais gerais (topo) */}
            <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
                <Card destaque titulo='Faturamento do mês' valor={fmtBRL(t.atual)} />
                <Card titulo='Total Contratado' valor={fmtBRL(t.contratado)} />
                <Card titulo={`Total Mês Anterior (${fmtComp(dados.competencia_anterior)})`} valor={t.anterior ? fmtBRL(t.anterior) : '—'} />
                <Card titulo='Variação Geral' valor={t.variacao == null ? '—' : `${t.variacao > 0 ? '+' : ''}${t.variacao.toFixed(2)}%`} />
            </div>

            <div className='flex flex-wrap items-end gap-3'>
                <div>
                    <label className='block text-xs text-gray-600 dark:text-gray-300 mb-1'>Competência</label>
                    <select className='border rounded-lg p-2 dark:bg-gray-700' value={competencia}
                        onChange={(e) => setCompetencia(e.target.value)}>
                        {(corrente ? listaCompetencias(corrente) : []).map((c, i) => (
                            <option key={c} value={i === 0 ? '' : c}>{fmtComp(`${c}-01`)}{i === 0 ? ' (corrente)' : ''}</option>
                        ))}
                    </select>
                    {loading && <span className='ml-2 text-xs text-gray-500'>carregando…</span>}
                </div>
                <div className='w-72'>
                    <label className='block text-xs text-gray-600 dark:text-gray-300 mb-1'>Buscar cliente ou CNPJ</label>
                    <input className='border rounded-lg p-2 w-full dark:bg-gray-700' value={busca}
                        onChange={(e) => setBusca(e.target.value)} placeholder='Nome ou CNPJ' />
                </div>
                <div>
                    <label className='block text-xs text-gray-600 dark:text-gray-300 mb-1'>Faturamento</label>
                    <select className='border rounded-lg p-2 dark:bg-gray-700' value={filtroTipo}
                        onChange={(e) => setFiltroTipo(e.target.value)}>
                        <option value=''>Todos</option>
                        <option value='Global'>Global</option>
                        <option value='Por Item'>Por Item</option>
                        <option value='Misto'>Misto</option>
                    </select>
                </div>
                <div className='ml-auto text-sm text-gray-500 dark:text-gray-400'>
                    Competência: <b>{fmtComp(dados.competencia_atual)}</b> · {clientes.length} cliente(s)
                    {dados.cobertura && <> · {dados.cobertura.contratos_com_ciclo} contrato(s) com ciclo · {dados.cobertura.contratos_em_previa} em prévia{dados.cobertura.contratos_sem_ciclo ? ` · ${dados.cobertura.contratos_sem_ciclo} sem ciclo` : ''}</>}
                </div>
            </div>

            {erro && <p className='text-sm text-red-600'>{erro} Os valores abaixo são de {fmtComp(dados.competencia_atual)}.</p>}

            <div className='overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700'>
                <table className='w-full text-sm'>
                    <thead className='bg-gray-50 dark:bg-gray-900'>
                        <tr>
                            {[
                                { key: 'cliente_nome', label: 'Cliente' },
                                { key: 'cnpj', label: 'CNPJ' },
                                { key: 'tipo_faturamento', label: 'Faturamento' },
                                { key: 'valor_contratado', label: 'Valor Contratado', num: true },
                                { key: 'valor_atual', label: 'Valor Atual', num: true },
                                { key: 'valor_anterior', label: 'Valor Mês Anterior', num: true },
                                { key: 'variacao', label: 'Variação', num: true },
                            ].map((col) => (
                                <th key={col.key}
                                    className={`${thCls} ${col.num ? 'text-right' : ''} cursor-pointer select-none hover:text-vox-blue-600`}
                                    title='Clique para ordenar'
                                    onClick={() => ordenar(col.key)}>
                                    {col.label}
                                    {ordem.key === col.key && <span className='ml-1'>{ordem.dir === 1 ? '▲' : '▼'}</span>}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className='divide-y divide-gray-200 dark:divide-gray-700'>
                        {clientes.map((c) => (
                            <React.Fragment key={c.cliente_id}>
                                <tr className='hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer dark:text-gray-200'
                                    onClick={() => setAbertos((a) => ({ ...a, [c.cliente_id]: !a[c.cliente_id] }))}>
                                    <td className='px-3 py-2 font-medium'>
                                        <span className='inline-block w-4 text-gray-400'>{abertos[c.cliente_id] ? '▾' : '▸'}</span>
                                        {c.cliente_nome}
                                        <span className='ml-2 text-xs text-gray-400'>({c.contratos.length} contrato{c.contratos.length > 1 ? 's' : ''})</span>
                                        {c.tem_evento_mes && <BadgeEvento eventos={c.contratos.flatMap((ct) => ct.eventos_mes || [])} />}
                                    </td>
                                    <td className='px-3 py-2 whitespace-nowrap'>{fmtCnpj(c.cnpj)}</td>
                                    <td className='px-3 py-2 whitespace-nowrap'>
                                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${c.tipo_faturamento === 'Global' ? 'bg-purple-100 text-purple-800' : c.tipo_faturamento === 'Misto' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}`}>
                                            {c.tipo_faturamento}
                                        </span>
                                    </td>
                                    <td className={tdNum}>{fmtBRL(c.valor_contratado)}</td>
                                    <td className={`${tdNum} font-semibold`}>{fmtBRL(c.valor_atual)}</td>
                                    <td className={tdNum}>{c.valor_anterior == null ? '—' : fmtBRL(c.valor_anterior)}</td>
                                    <td className={`${tdNum}`}><Variacao v={c.variacao} /></td>
                                </tr>
                                {abertos[c.cliente_id] && c.contratos.map((ct) => (
                                    <tr key={`${c.cliente_id}-${ct.id}`} className='bg-gray-50/60 dark:bg-gray-900/40 dark:text-gray-300'>
                                        <td className='pl-10 pr-3 py-1.5'>
                                            {ct.numero ? `${ct.numero} — ` : ''}{ct.nome}
                                            <BadgeEvento eventos={ct.eventos_mes} />
                                            {ct.origem === 'previa' && <span className='ml-2 inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700' title='Ainda sem ciclo neste mês: valor calculado na hora com a mesma conta da fatura'>prévia</span>}
                                        </td>
                                        <td className='px-3 py-1.5'></td>
                                        <td className='px-3 py-1.5 whitespace-nowrap'>
                                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${ct.tipo === 'global' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                                                {ct.tipo === 'global' ? 'Global' : 'Por Item'}
                                            </span>
                                        </td>
                                        <td className={tdNum}>{fmtBRL(ct.valor_contratado)}</td>
                                        <td className={tdNum}>{fmtBRL(ct.valor_atual)}</td>
                                        <td className={tdNum}>{ct.valor_anterior == null ? '—' : fmtBRL(ct.valor_anterior)}</td>
                                        <td className={tdNum}><Variacao v={ct.variacao} /></td>
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
            <p className='text-xs text-gray-400'>
                Faturamento do mês = soma dos ciclos de faturamento da competência (todo contrato ativo tem um por mês; contratos sem e-mail ganham um ciclo interno no fechamento).
                Na competência corrente, contrato ainda sem ciclo aparece como "prévia", com a mesma conta da fatura: proporcional de reajuste/aditivo/supressão, Homologação da Instalação (cobra dos dias restantes no mês da homologação) e cobrança única (uma vez só).
                Contratado: global = Valor Global; por item = soma dos itens implantados. Mês Anterior: ciclo do mês anterior (antes de set/2026, o critério antigo).
            </p>
        </div>
    );
};

export default FaturamentoTotal;
